<script setup lang="ts">
import { useI18n } from '@/composable/useI18n'
import type { ISensorChartOverlayAlarm } from '@/interfaces/components/sensorChartAlarm'
import { translateAlarmType } from '@/interfaces/enums'
import { computed, ref } from 'vue'

const props = defineProps<{
  alarms: ISensorChartOverlayAlarm[]
  isLoading: boolean
  accent: 'red' | 'orange' | 'purple'
  expanded: boolean
}>()

const emit = defineEmits<{
  (e: 'update:expanded', value: boolean): void
  (e: 'select-alarm', alarm: ISensorChartOverlayAlarm): void
  (e: 'hover-alarm', alarm: ISensorChartOverlayAlarm | null): void
}>()

const { t } = useI18n()
const hoveredAlarmId = ref<number | null>(null)

const sensorAlarms = computed(() => props.alarms.filter((alarm) => alarm.origin === 'sensor'))
const groupAlarms = computed(() => props.alarms.filter((alarm) => alarm.origin === 'group'))

const sectionAccentClass = computed(() => {
  if (props.isLoading) return 'border-blue-300 bg-blue-50/50 shadow-sm'
  if (props.alarms.length === 0) return 'border-blue-300 bg-slate-50/80 shadow-sm'
  if (props.accent === 'orange') return 'border-orange-400 bg-orange-50/60 shadow-md'
  if (props.accent === 'purple') return 'border-purple-400 bg-purple-50/60 shadow-md'
  return 'border-red-400 bg-red-50/60 shadow-md'
})

const titleAccentClass = computed(() => {
  if (props.alarms.length === 0) return 'text-slate-700'
  if (props.accent === 'orange') return 'text-orange-800'
  if (props.accent === 'purple') return 'text-purple-800'
  return 'text-red-800'
})

const sensorSectionTitleClass = computed(() => {
  if (props.accent === 'orange') return 'text-orange-600'
  if (props.accent === 'purple') return 'text-purple-600'
  return 'text-red-600'
})

function isCorrelationalAlarm(alarm: ISensorChartOverlayAlarm): boolean {
  return alarm.alarm_type === 'correlational'
}

function isSensorCardHovered(alarm: ISensorChartOverlayAlarm): boolean {
  return hoveredAlarmId.value === alarm.id
}

function groupCardClass(alarm: ISensorChartOverlayAlarm): string {
  return isCorrelationalAlarm(alarm)
    ? 'border-purple-200 hover:border-purple-400'
    : 'border-yellow-200 hover:border-yellow-400'
}

function groupHeaderClass(alarm: ISensorChartOverlayAlarm): string {
  return isCorrelationalAlarm(alarm)
    ? 'border-l-4 border-purple-500 bg-purple-100/40 px-2 py-1'
    : 'border-l-4 border-yellow-400 bg-yellow-100/40 px-2 py-1'
}

function groupTitleClass(alarm: ISensorChartOverlayAlarm): string {
  return isCorrelationalAlarm(alarm) ? 'text-purple-700' : 'text-yellow-600'
}

function sensorCardClass(alarm: ISensorChartOverlayAlarm): string {
  const isHovered = isSensorCardHovered(alarm)
  if (isCorrelationalAlarm(alarm)) {
    return isHovered
      ? 'border-purple-500 ring-2 ring-purple-400/60 shadow-md'
      : 'border-purple-200 hover:shadow-md'
  }
  if (props.accent === 'orange') {
    return isHovered
      ? 'border-orange-500 ring-2 ring-orange-400/60 shadow-md'
      : 'border-orange-200 hover:shadow-md'
  }
  if (props.accent === 'purple') {
    return isHovered
      ? 'border-purple-500 ring-2 ring-purple-400/60 shadow-md'
      : 'border-purple-200 hover:shadow-md'
  }
  return isHovered
    ? 'border-red-500 ring-2 ring-red-400/60 shadow-md'
    : 'border-red-200 hover:shadow-md'
}

function sensorHeaderClass(alarm: ISensorChartOverlayAlarm): string {
  if (isCorrelationalAlarm(alarm)) {
    return 'border-l-4 border-purple-500 bg-purple-50/40 px-2 py-1'
  }
  if (props.accent === 'orange') {
    return 'border-l-4 border-orange-500 bg-orange-50/40 px-2 py-1'
  }
  if (props.accent === 'purple') {
    return 'border-l-4 border-purple-500 bg-purple-50/40 px-2 py-1'
  }
  return 'border-l-4 border-red-500 bg-red-50/40 px-2 py-1'
}

function sensorTitleClass(alarm: ISensorChartOverlayAlarm): string {
  if (isCorrelationalAlarm(alarm)) return 'text-purple-800'
  if (props.accent === 'orange') return 'text-orange-800'
  if (props.accent === 'purple') return 'text-purple-800'
  return 'text-red-800'
}

function formatAlarmDate(dateStr: string | null): string {
  if (!dateStr) return t('sensor.chart_alarms.open_ended')
  const d = new Date(dateStr)
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function formatAlarmDuration(startStr: string | null, endStr: string | null): string {
  if (!startStr || !endStr) return ''
  const start = new Date(startStr).getTime()
  const end = new Date(endStr).getTime()
  const diffMs = Math.max(0, end - start)
  const totalMin = Math.floor(diffMs / 60000)
  const d = Math.floor(totalMin / 1440)
  const restMin = totalMin % 1440
  const h = Math.floor(restMin / 60)
  const m = restMin % 60
  if (d > 0) return `${d}d ${h}h ${m} min`
  if (h > 0) return `${h}h ${m} min`
  return `${totalMin} min`
}

function toggleExpanded() {
  emit('update:expanded', !props.expanded)
}

function onHover(alarm: ISensorChartOverlayAlarm | null) {
  hoveredAlarmId.value = alarm?.id ?? null
  emit('hover-alarm', alarm)
}
</script>

<template>
  <section class="mb-3 border-2 transition-all" :class="sectionAccentClass">
    <div
      class="flex cursor-pointer items-center justify-between p-2"
      role="button"
      tabindex="0"
      :aria-expanded="expanded"
      @click="toggleExpanded"
      @keydown.enter.space.prevent="toggleExpanded">
      <h4 class="text-sm font-semibold" :class="titleAccentClass">
        {{ t('sensor.chart_alarms.alarms_found') }}
        <span v-if="!isLoading" class="ml-1.5 font-normal text-slate-500">({{ alarms.length }})</span>
      </h4>
      <button
        type="button"
        class="flex h-8 w-8 shrink-0 items-center justify-center border-0 bg-transparent text-slate-600 transition-colors hover:bg-slate-200/60 hover:text-slate-800"
        :title="expanded ? t('sensor.chart_alarms.collapse') : t('sensor.chart_alarms.expand')"
        @click.stop="toggleExpanded">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 transition-transform"
          :class="expanded ? '' : 'rotate-180'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
    <div v-show="expanded" class="border-t border-slate-200/80 p-2 pt-1.5">
      <div v-if="isLoading" class="flex items-center gap-2 py-3 text-sm text-blue-700">
        <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
        {{ t('sensor.chart_alarms.searching') }}
      </div>
      <div v-else-if="alarms.length === 0" class="py-4 text-center text-sm text-slate-500">
        {{ t('sensor.chart_alarms.no_alarms') }}
      </div>
      <div v-else class="flex flex-col">
        <div v-if="groupAlarms.length > 0">
          <span class="mb-3 block text-sm font-semibold text-yellow-600">
            {{ t('sensor.chart_alarms.group_section') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="alarm in groupAlarms"
              :key="alarm.id"
              role="button"
              tabindex="0"
              class="flex min-w-[10rem] max-w-[12rem] flex-1 cursor-pointer flex-col border bg-white shadow-sm transition-all duration-150 hover:shadow-md"
              :class="groupCardClass(alarm)"
              @click="emit('select-alarm', alarm)"
              @keydown.enter.prevent="emit('select-alarm', alarm)"
              @keydown.space.prevent="emit('select-alarm', alarm)">
              <div :class="groupHeaderClass(alarm)">
                <p v-if="alarm.alarm_type == 'correlational'"
                  class="truncate text-xs font-semibold uppercase tracking-wide"
                  :class="groupTitleClass(alarm)"
                  :title="alarm.name ?? translateAlarmType(alarm.alarm_type)">
                  {{ alarm.name ?? translateAlarmType(alarm.alarm_type) }}
                </p>
                <p v-if="alarm.alarm_type != 'correlational'"
                  class="truncate text-xs font-semibold uppercase tracking-wide"
                  :class="groupTitleClass(alarm)"
                  :title="translateAlarmType(alarm.alarm_type)">
                  {{ translateAlarmType(alarm.alarm_type) }}
                </p>
                <p
                  v-if="alarm.group?.name"
                  class="mt-0.5 truncate text-[10px] font-medium"
                  :class="groupTitleClass(alarm)"
                  :title="alarm.group.name">
                  {{ t('sensor.chart_alarms.group_badge', { name: alarm.group.name }) }}
                </p>
              </div>
              <div class="flex flex-col gap-0.5 px-2 py-1.5">
                <div class="flex justify-between text-[11px] text-slate-600">
                  <span class="pr-1 text-slate-500">{{ t('sensor.chart_alarms.classification') }}</span>
                  <span class="max-w-[7rem] truncate font-medium text-slate-800">
                    {{ alarm.classification_type?.name ?? '-' }}
                  </span>
                </div>
                <div class="flex justify-between text-[11px] text-slate-600">
                  <span class="text-slate-500">{{ t('sensor.chart_alarms.start') }}</span>
                  <span class="font-medium tabular-nums">{{ formatAlarmDate(alarm.start_date) }}</span>
                </div>
                <div class="flex justify-between text-[11px] text-slate-600">
                  <span class="text-slate-500">{{ t('sensor.chart_alarms.end') }}</span>
                  <span class="font-medium tabular-nums">{{ formatAlarmDate(alarm.end_date) }}</span>
                </div>
                <p
                  v-if="formatAlarmDuration(alarm.start_date, alarm.end_date)"
                  class="mt-1 border-t border-slate-100 pt-1 text-[11px] font-semibold text-slate-700">
                  {{ formatAlarmDuration(alarm.start_date, alarm.end_date) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="sensorAlarms.length > 0" :class="groupAlarms.length > 0 ? 'mt-6' : ''">
          <span class="mb-3 block text-sm font-semibold" :class="sensorSectionTitleClass">
            {{ t('sensor.chart_alarms.sensor_section') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="alarm in sensorAlarms"
              :key="alarm.id"
              role="button"
              tabindex="0"
              class="flex min-w-[10rem] max-w-[12rem] flex-1 cursor-pointer flex-col border bg-white shadow-sm transition-all duration-150"
              :class="sensorCardClass(alarm)"
              @mouseenter="onHover(alarm)"
              @mouseleave="onHover(null)"
              @click="emit('select-alarm', alarm)"
              @keydown.enter.prevent="emit('select-alarm', alarm)"
              @keydown.space.prevent="emit('select-alarm', alarm)">
              <div :class="sensorHeaderClass(alarm)">
                <p v-if="alarm.alarm_type === 'correlational'"
                  class="truncate text-xs font-semibold uppercase tracking-wide text-purple-700"
                  :title="alarm.name ?? translateAlarmType(alarm.alarm_type)">
                  {{ alarm.name ?? translateAlarmType(alarm.alarm_type) }}
                </p>
                <p v-if="alarm.alarm_type != 'correlational'"
                  class="truncate text-xs font-semibold uppercase tracking-wide"
                  :class="sensorTitleClass(alarm)"
                  :title="translateAlarmType(alarm.alarm_type)">
                  {{ translateAlarmType(alarm.alarm_type) }}
                </p>
              </div>
              <div class="flex flex-col gap-0.5 px-2 py-1.5">
                <div class="flex justify-between text-[11px] text-slate-600">
                  <span class="pr-1 text-slate-500">{{ t('sensor.chart_alarms.classification') }}</span>
                  <span class="max-w-[7rem] truncate font-medium text-slate-800">
                    {{ alarm.classification_type?.name ?? '-' }}
                  </span>
                </div>
                <div class="flex justify-between text-[11px] text-slate-600">
                  <span class="text-slate-500">{{ t('sensor.chart_alarms.start') }}</span>
                  <span class="font-medium tabular-nums">{{ formatAlarmDate(alarm.start_date) }}</span>
                </div>
                <div class="flex justify-between text-[11px] text-slate-600">
                  <span class="text-slate-500">{{ t('sensor.chart_alarms.end') }}</span>
                  <span class="font-medium tabular-nums">{{ formatAlarmDate(alarm.end_date) }}</span>
                </div>
                <p
                  v-if="formatAlarmDuration(alarm.start_date, alarm.end_date)"
                  class="mt-1 border-t border-slate-100 pt-1 text-[11px] font-semibold text-slate-700">
                  {{ formatAlarmDuration(alarm.start_date, alarm.end_date) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
